import { IExtendedAddress } from '../global/extendedAddress';
import { IPaginationParams } from '../global/pagination';
import { ISortParams } from '../global/sort';
import { IPosition } from '../global/position';

export enum InvoicingFrequencyType {
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
}

export interface IPartner {
  id: number;
  organizationId: number;
  name: string;
  emailThemeId: number;
  internal: boolean;
  paymentTermsDays: number;
  businessModel: string;
  partnerType: string;
  paymentOfInvoiceAllowed: boolean;
  postalAddress: IExtendedAddress;
  invoiceAddress: IExtendedAddress;
  invoicingCurrencyCode: string;
  invoicingFrequency: string;
  mainLanguageIsoCode: string;
  journeyFreeCancellationWindowHours: number;
  vatNumber: string;
  whiteLabel: IWhiteLabel;
  communicationPreferences: ICommunicationPreferences;
  styleConfiguration?: IStyleConfiguration;
  ibanCode: string;
  ibanAccountHolder: string;
  swiftCode: string;
}

export interface IPartnersRequestParams extends IPaginationParams, ISortParams {
  name: string;
  invoicingFrequency: InvoicingFrequencyType;
  updatedAfter: string;
}

export interface IPartnerGeoZone {
  id: number;
  name: string;
  polygon: IPosition[];
}

export interface IPartnerGeoZoneInRadius {
  lat: number;
  lng: number;
  radiusInKm: number;
}

export interface IPartnerGeoZoneRequestParams extends IPaginationParams, ISortParams {
  name: string;
}

export interface ITagManager {
  tagManagerId: string;
}

export interface IWhiteLabelSettings {
  showHubFirst: boolean;
}

export interface IWhiteLabel {
  id?: number;
  domainName?: string;
  fullDomainName?: string;
  partnerId?: number;
  active: boolean;
  settings: IWhiteLabelSettings;
  tagManagers: ITagManager[];
  helpdeskUrl?: string;
  onlineBookingToolEnabled?: boolean;
  allowTravellersEditingJourneys?: boolean;
}

export interface IWhiteLabelParams {
  id?: number;
  partnerId?: number;
  domainName?: string;
  fullDomainName?: string;
  settings: IWhiteLabelSettings;
  tagManagers: string[];
  helpdeskUrl?: string;
  onlineBookingToolEnabled?: boolean;
  allowTravellersEditingJourneys?: boolean;
}

export const PartnerInvoicingFrequencyDisplayName = {
  [InvoicingFrequencyType.Weekly]: 'Weekly',
  [InvoicingFrequencyType.Monthly]: 'Monthly',
};

export interface IAccessiblePartner {
  permissions: string[];
  partnerId: number;
  partnerName: string;
}

export interface ICommunicationPreferences {
  sendBookingPlacedEmail: boolean;
  sendJourneyConfirmedEmail: boolean;
  sendJourneyPlannedEmail: boolean;
  receiveWebhookEvents: boolean;
  sendJourneyCancellationEmails: boolean;
  sendJourneyCancellationEmailsToTravellers: boolean;
  sendSurveyEmailToTravellers: boolean;
  webhook: string;
  sendPaymentLinks: boolean;
  customerServiceEmail: string;
  sendPrePickupSMS: boolean;
  sendBookingCreatedSMS: boolean;
  emailSenderAddress: string;
  emailSenderName: string;
}

export interface IStyleConfiguration {
  backgroundColor: string;
  backgroundContrastColor: string;
  primaryColor: string;
  primaryContrastColor: string;
  secondaryColor: string;
  secondaryContrastColor: string;
  tertiaryColor: string;
  tertiaryContrastColor: string;
  heroImageUrl: string;
  logoUrl: string;
}

export enum StylingSettingType {
  WhiteLabel = 'WHITE_LABEL',
  Email = 'EMAIL',
  Voucher = 'VOUCHER',
}

export interface IStylingSettingParams {
  partnerId: number;
  code: string;
  type: string;
  value: string;
  id: number;
}

export interface IStylingSettingsRequestParams extends IPaginationParams, ISortParams {
  partnerId: number;
  code: string;
  type: string;
}

export interface IStylingSetting {
  id: number;
  partnerId: number;
  code: string;
  type: string;
  value: string;
}

export enum BusinessModelType {
  Commission = 'COMMISSION',
  MarkUp = 'MARK_UP',
}

export const BusinessModelDisplayName = {
  [BusinessModelType.Commission]: 'Commission',
  [BusinessModelType.MarkUp]: 'Markup',
};
